import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Post1 } from 'components/blog';
import BlogImage2 from 'assets/images/hero/New folder/BlogImage2.png';
import { ArrowRight, Calendar } from "lucide-react";
import { colors } from 'styles/colors';

type BlogPostProps = {
    Post1: Post1;
};

const BlogPost2 = ({ Post1 }: BlogPostProps) => {

    const formatTitleForURL = (title?: string) =>
        title
            ? title
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .trim()
            : '';

    const blogUrl = `/blog/${formatTitleForURL(Post1.title)}`;

    const truncateText = (text: string | undefined, maxLength: number) => {
        if (!text) return "";
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };

    return (
        <article
            className="group d-flex flex-column h-100 rounded-xl overflow-hidden mb-1"
            style={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(36,52,125,0.12), 0 8px 16px rgba(38,162,171,0.08)';
                e.currentTarget.style.borderColor = colors.primary.teal;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                e.currentTarget.style.borderColor = '#e5e7eb';
            }}
        >
            {/* Image */}
            <Link to={blogUrl} className="d-block position-relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                <img
                    src={Post1.image || BlogImage2}
                    alt={Post1.title}
                    className="w-100 h-100"
                    style={{
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                />
                {/* Tag overlay on image */}
                {Post1.tag && (
                    <Badge
                        bg=""
                        style={{
                            backgroundColor: colors.primary.teal1,
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            boxShadow: '0 2px 8px rgba(45,212,191,0.3)',
                        }}
                        className="text-white"
                    >
                        {Post1.tag}
                    </Badge>
                )}
            </Link>

            {/* Content */}
            <div className="d-flex flex-column flex-grow-1 p-3 p-md-4">
                {/* Date */}
                <div className="d-flex align-items-center mb-2" style={{ gap: '6px' }}>
                    <Calendar size={14} style={{ color: colors.primary.teal }} />
                    <time
                        dateTime={Post1.createdOn}
                        style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}
                    >
                        {new Date(Post1.createdOn).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                        })}
                    </time>
                </div>

                {/* Title */}
                <h4
                    className="mb-2"
                    style={{
                        fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
                        fontWeight: 700,
                        lineHeight: 1.4,
                        color: colors.primary.darkBlue,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    <Link
                        to={blogUrl}
                        style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = colors.primary.teal; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = colors.primary.darkBlue; }}
                    >
                        {Post1.title}
                    </Link>
                </h4>

                {/* Description */}
                <p
                    className="mb-0 flex-grow-1"
                    style={{
                        fontSize: '0.875rem',
                        lineHeight: 1.7,
                        color: '#6b7280',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {truncateText(Post1.description, 120)}
                </p>

                {/* Divider + Read More */}
                <div
                    className="mt-3 pt-3 d-flex align-items-center justify-content-end"
                    style={{ borderTop: '1px solid #f3f4f6' }}
                >
                    <Link
                        to={blogUrl}
                        state={{ id: Post1.id }}
                        className="d-flex align-items-center text-decoration-none"
                        style={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: colors.primary.darkBlue,
                            transition: 'color 0.2s, gap 0.2s',
                            gap: '4px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = colors.primary.teal;
                            e.currentTarget.style.gap = '8px';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = colors.primary.darkBlue;
                            e.currentTarget.style.gap = '4px';
                        }}
                    >
                        Read More
                        <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default BlogPost2;

